import './videoForm.scss';
import { isSystem, lang } from '@umu/ukit-lang';

import * as React from 'react';
const { useState, useMemo } = React;
import { Editor } from '@tiptap/react';
import { FormModal } from '../modal';
import { components } from 'common';
const { If } = components;
import { Iconfont, SnackBar, Tooltip } from '@umu/umu-design';
import { UmuVideoSelectorPC } from 'businessCommon/src/components';
import { IMediaData } from '@umu/umu-api/src/services/resource/getResourceArr';
import { E_PLATFORM } from '../../interface';

interface IVideoFormProps {
    editor: Editor;
    platform: E_PLATFORM;
    visible?: boolean;
    className?: string;
    done: VoidFunction;
}

enum VIDEO_TYPE {
    UNKNOW,
    UMU,
    YOUTUBE,
    IFRAME,
}

const isValidYoutubeUrl = (src: string) => {
    return (
        src.startsWith('https://youtu.be') ||
        src.startsWith('https://www.youtube.com/watch') ||
        src.startsWith('https://www.youtube.com/embed')
    );
};
const isValidQQUrl = (src: string) => {
    return /^https:\/\/v\.qq\.com/.test(src);
};
const isValidBilibiliUrl = (src: string) => {
    return /^https:\/\/www\.bilibili\.com\/video/.test(src) || /^https:\/\/player\.bilibili\.com/.test(src);
};
const isValidVimeoUrl = (src: string) => {
    return /^https:\/\/vimeo\.com/.test(src) || /^https:\/\/player\.vimeo\.com/.test(src);
};
const isValidMicrosoftstreamUrl = (src: string) => {
    return /^https:\/\/web\.microsoftstream\.com\/video/.test(src);
};

const getVideoFrom = (src: string) => {
    if (isValidYoutubeUrl(src)) {
        return VIDEO_TYPE.YOUTUBE;
    }
    if (isValidQQUrl(src) || isValidBilibiliUrl(src) || isValidVimeoUrl(src) || isValidMicrosoftstreamUrl(src)) {
        return VIDEO_TYPE.IFRAME;
    }
    return VIDEO_TYPE.UNKNOW;
};

// 'https://statics-umu-test.umucdn.cn/resource/T/GYIT/r4Uuu/transcoding/455577548.mov.mp4'
// 'https://vimeo.com/930155433?share=copy'
// 'https://v.qq.com/x/page/f3544ki3yqj.html?url_from=share'
// 'https://www.bilibili.com/video/BV1nt42157XS/?spm_id_from=333.1007.tianma.1-2-2.click'
// 'https://www.youtube.com/embed/3lTUAWOgoHs?controls=0'

export const VideoForm = ({ className, editor, platform, done }: IVideoFormProps) => {
    const [video, setVideo] = useState({
        src: '',
        from: VIDEO_TYPE.UMU,
    });
    const [showUmuVideoSelect, setShowUmuVideoSelect] = useState(false);
    const videoFromByLang = isSystem('cn') ? '腾讯视频，Bilibili' : 'Youtube, Vimeo';
    const videoLinkTip = useMemo(() => lang('仅支持 {$0} 的链接地址', videoFromByLang), []);

    const validateVideo = () => {
        const src = video.src;
        if (!src || video.from === VIDEO_TYPE.UNKNOW) {
            return false;
        }
        return true;
    };

    const handleOk = () => {
        const src = video.src;
        const videoType = video.from;
        if (!validateVideo()) {
            SnackBar.show(!src ? lang('请输入链接内容') : videoLinkTip);
            return;
        }
        if (videoType === VIDEO_TYPE.YOUTUBE) {
            editor.commands.setYoutubeVideo({ src });
        } else {
            editor.commands.setIframe(src, { width: '100%' });
        }
        done();
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const src = e.target.value.trim();
        setVideo({
            src,
            from: getVideoFrom(src),
        });
    };

    const selectUmuVideoDone = React.useCallback((file: IMediaData) => {
        editor.commands.setVideo(file.transcoding_url, { height: 'auto', width: '100%' });
        done();
    }, []);

    return (
        <>
            <FormModal
                onOk={handleOk}
                onCancel={done}
                title={lang('插入视频')}
                okText={lang('确认')}
                cancelText={lang('取消')}
                platform={platform}
                className={className}
            >
                <div className="rich-text-comp__video-form">
                    <div className="menu-form-item">
                        <div className="menu-form-item__label flex--start--center">
                            {lang('视频链接地址')}
                            <Tooltip
                                color="#fff"
                                zIndex={100000}
                                title={
                                    <div className="select-umu-video-tip">
                                        {lang(
                                            '请检查视频链接是否包含广告，广告来源于第三方视频网站，UMU 学习平台不会增加广告。如果不希望看到广告，可以上传视频。',
                                        )}
                                    </div>
                                }
                            >
                                <Iconfont type="tips" />
                            </Tooltip>
                        </div>
                        <input
                            className="menu-form-item__input"
                            type="text"
                            onChange={onInputChange}
                            autoFocus
                            placeholder={lang('请输入视频链接地址')}
                        />
                        <div
                            className={`video-from-tip ${
                                video.from === VIDEO_TYPE.UNKNOW ? 'menu-form-item__err' : ''
                            }`}
                        >
                            {videoLinkTip}
                        </div>
                    </div>
                    <div className="menu-form-item menu-form-item_upload">
                        <div className="menu-form-item__label">
                            {platform === E_PLATFORM.mobile
                                ? lang('如果要上传本地视频，请到 PC 端浏览器进行操作')
                                : lang('或直接上传视频')}
                        </div>
                        <If condition={platform !== E_PLATFORM.mobile}>
                            <button
                                className="btn-select-umu-video flex--center"
                                onClick={() => setShowUmuVideoSelect(true)}
                            >
                                <Iconfont type="Submit" />
                                {lang('上传视频')}
                            </button>
                        </If>
                    </div>
                </div>
            </FormModal>
            {showUmuVideoSelect && (
                <UmuVideoSelectorPC cancel={() => setShowUmuVideoSelect(false)} done={selectUmuVideoDone} />
            )}
        </>
    );
};
